// Project 7: 팩트 체크 결과 컴포넌트
import type { FactCheckResult as FactCheckResultType } from '@/types/factCheck';

interface FactCheckResultProps {
  result: FactCheckResultType;
}

export function FactCheckResult({ result }: FactCheckResultProps) {
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return { bg: '#fee', border: '#f00', text: '#c00' };
      case 'medium':
        return { bg: '#ffd', border: '#fa0', text: '#a60' };
      case 'low':
        return { bg: '#eef', border: '#00f', text: '#006' };
      default:
        return { bg: '#eee', border: '#999', text: '#333' };
    }
  };

  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case 'high':
        return '높음 ⚠️';
      case 'medium':
        return '중간 ⚡';
      case 'low':
        return '낮음 ℹ️';
      default:
        return '알 수 없음';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return '#00b894';
    if (score >= 70) return '#fdcb6e';
    return '#d63031';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 90) return '#00b894';
    if (score >= 70) return '#fdcb6e';
    return '#d63031';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* 신뢰도 점수 */}
      <div style={{
        background: '#fff',
        padding: '1.5rem',
        borderRadius: 8,
        border: '1px solid #ddd'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 600 }}>
          🔍 팩트 체크 결과
        </h3>

        <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
          {/* 점수 */}
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '4rem',
              fontWeight: 800,
              color: getScoreColor(result.trustScore),
              lineHeight: 1
            }}>
              {result.trustScore}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
              신뢰도 점수
            </div>
          </div>

          {/* 게이지 */}
          <div style={{ flex: 1 }}>
            <div style={{
              height: '2rem',
              background: '#e0e0e0',
              borderRadius: '999px',
              overflow: 'hidden'
            }}>
              <div
                style={{
                  height: '100%',
                  background: getScoreBgColor(result.trustScore),
                  width: `${result.trustScore}%`,
                  transition: 'width 0.3s ease'
                }}
              />
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '0.75rem',
              color: '#666',
              marginTop: '0.5rem'
            }}>
              <span>낮음</span>
              <span>중간</span>
              <span>높음</span>
            </div>
          </div>
        </div>

        {/* 통계 */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1rem',
          marginTop: '1.5rem'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '0.75rem',
            background: '#f8f9fa',
            borderRadius: 6
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>
              {result.numbersFound}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>
              숫자/통계
            </div>
          </div>
          <div style={{
            textAlign: 'center',
            padding: '0.75rem',
            background: '#f8f9fa',
            borderRadius: 6
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>
              {result.datesFound}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>
              날짜/시간
            </div>
          </div>
          <div style={{
            textAlign: 'center',
            padding: '0.75rem',
            background: '#f8f9fa',
            borderRadius: 6
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>
              {result.quotesFound}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>
              인용문
            </div>
          </div>
        </div>
      </div>

      {/* 이슈 목록 */}
      {result.issues.length > 0 && (
        <div style={{
          background: '#fff',
          padding: '1.5rem',
          borderRadius: 8,
          border: '1px solid #ddd'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 600 }}>
            ⚠️ 검증이 필요한 항목 ({result.issues.length}개)
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {result.issues.map((issue, index) => {
              const colors = getSeverityColor(issue.severity);
              return (
                <div
                  key={index}
                  style={{
                    padding: '1rem',
                    border: `1px solid ${colors.border}`,
                    borderRadius: 6,
                    background: colors.bg
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'start',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontWeight: 600, color: colors.text }}>
                        {getSeverityLabel(issue.severity)}
                      </span>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        borderRadius: 4,
                        background: '#fff',
                        color: '#666'
                      }}>
                        {issue.category}
                      </span>
                    </div>
                  </div>

                  <div style={{ marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 600 }}>의심 내용:</span>
                    <span style={{ marginLeft: '0.5rem' }}>{issue.text}</span>
                  </div>

                  <div>
                    <span style={{ fontWeight: 600 }}>이유:</span>
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem' }}>
                      {issue.reason}
                    </span>
                  </div>

                  {issue.requiresDisclaimer && (
                    <div style={{
                      marginTop: '0.5rem',
                      fontSize: '0.75rem',
                      color: '#0066cc',
                      background: '#e6f2ff',
                      padding: '0.5rem',
                      borderRadius: 4
                    }}>
                      해당 문장은 개인 의견/추정임을 명시해야 합니다.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 이슈 없음 */}
      {result.issues.length === 0 && (
        <div style={{
          background: '#d4edda',
          border: '1px solid #c3e6cb',
          padding: '1.5rem',
          borderRadius: 8,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✅</div>
          <div style={{ fontWeight: 600, color: '#155724', fontSize: '1.125rem' }}>
            팩트 체크 완료!
          </div>
          <div style={{ fontSize: '0.875rem', color: '#155724', marginTop: '0.25rem' }}>
            의심스러운 항목이 발견되지 않았습니다.
          </div>
        </div>
      )}
    </div>
  );
}

